import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "../../../CustomHooks/useApi";
import { CreditCard, Loader2 } from "lucide-react";

const AepsSettlement3 = () => {
    const navigate = useNavigate();

    const [mobile, setMobile] = useState("");
    const [otp, setOtp] = useState("");
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");

    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [showRegister, setShowRegister] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [mobileLocked, setMobileLocked] = useState(false);

    // APIs
    const { callApi: checkStatus } = useApi("/payout/status", "GET");
    const { callApi: sendOtpApi } = useApi("/payout/send-otp", "POST");
    const { callApi: verifyOtpApi } = useApi("/payout/verify-otp", "POST");

    /* ================= CHECK CUSTOMER STATUS ================= */
    const handleCheckMobile = async () => {
        if (!/^[6-9]\d{9}$/.test(mobile)) {
            setMessage("Enter valid 10 digit mobile");
            setMessageType("error");
            return;
        }

        setIsLoading(true);
        setMessage("");

        try {
            const res = await checkStatus(null, `?mobile=${mobile}`);

            if (res?.isVerified) {
                navigate("/aepsremitter3", {
                    state: { mobile, name },
                });
            } else {
                setShowRegister(true);
                setMobileLocked(true);
            }
        } catch (err) {
            console.error(err);
            setMessage("Server error");
            setMessageType("error");
        }

        setIsLoading(false);
    };

    /* ================= SEND OTP (NO REGISTER API) ================= */
    const handleSendOtp = async () => {
        if (!name || !address) {
            setMessage("Enter name and address");
            setMessageType("error");
            return;
        }

        setIsLoading(true);
        setMessage("");

        try {
            const res = await sendOtpApi({ mobile });

            if (res?.status === "OTP_SENT") {
                setShowOtp(true);
                setMessage(res.message || "OTP sent successfully");
                setMessageType("success");
            } else {
                setMessage("OTP send failed");
                setMessageType("error");
            }
        } catch (err) {
            console.error(err);
            setMessage("OTP send failed");
            setMessageType("error");
        }

        setIsLoading(false);
    };

    /* ================= VERIFY OTP (WITH NAME + ADDRESS) ================= */
    const handleVerifyOtp = async () => {
        if (otp.length !== 6) {
            setMessage("Enter valid 6 digit OTP");
            setMessageType("error");
            return;
        }

        setIsLoading(true);
        setMessage("");

        try {
            const res = await verifyOtpApi({
                mobile,
                otp,
                name,
                address,
            });

            if (res?.status === "VERIFIED" && res?.isVerified) {
                setMessage("Customer verified successfully");
                setMessageType("success");

                setTimeout(() => {
                    navigate("/aepsremitter2", {
                        state: { mobile, name },
                    });
                }, 1000);
            } else {
                setMessage("Invalid OTP");
                setMessageType("error");
            }
        } catch (err) {
            console.error(err);
            setMessage("OTP verify failed");
            setMessageType("error");
        }

        setIsLoading(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-cyan-50 py-10 px-4">
            <div className="max-w-2xl mx-auto space-y-8">

                {/* Header */}
                <div className="text-center">
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-cyan-500 flex items-center justify-center text-white shadow-lg">
                        <CreditCard className="w-7 h-7" />
                    </div>
                    <h1 className="mt-4 text-3xl font-semibold text-slate-900">
                        AEPS Settlement (UPI)
                    </h1>
                    <p className="mt-1 text-slate-600">
                        Verify customer and proceed with secure UPI transfers
                    </p>
                </div>

                {/* Main Card */}
                <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-slate-200 shadow-xl p-8 space-y-4">

                    {/* Mobile Input */}
                    <div>
                        <label className="text-sm font-medium">Mobile Number</label>
                        <input
                            value={mobile}
                            maxLength={10}
                            disabled={mobileLocked}
                            onChange={(e) => /^\d*$/.test(e.target.value) && setMobile(e.target.value)}
                            className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 disabled:bg-gray-100"
                            placeholder="10 digit mobile"
                        />
                    </div>

                    {/* Registration Fields (Name + Address) */}
                    {showRegister && (
                        <div className="space-y-3">
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Customer Name"
                                className="w-full px-4 py-3 border rounded-xl"
                            />

                            <textarea
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder="Address"
                                className="w-full px-4 py-3 border rounded-xl"
                            />

                            <button
                                onClick={handleSendOtp}
                                className="w-full py-3 bg-indigo-600 text-white rounded-xl font-semibold"
                            >
                                {isLoading ? "Sending OTP..." : "Send OTP"}
                            </button>
                        </div>
                    )}

                    {/* OTP Section */}
                    {showOtp && (
                        <div className="space-y-3">
                            <label className="text-sm font-medium">Enter OTP</label>
                            <input
                                value={otp}
                                maxLength={6}
                                onChange={(e) => /^\d*$/.test(e.target.value) && setOtp(e.target.value)}
                                className="w-full px-4 py-3 border rounded-xl"
                            />

                            <button
                                onClick={handleVerifyOtp}
                                className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                            >
                                {isLoading && <Loader2 className="animate-spin w-5 h-5" />}
                                Verify OTP
                            </button>
                        </div>
                    )}

                    {/* Message Alert */}
                    {message && (
                        <div
                            className={`px-4 py-3 rounded-xl text-sm ${messageType === "success"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                                }`}
                        >
                            {message}
                        </div>
                    )}

                    {/* Check Customer Button */}
                    {!showRegister && !showOtp && (
                        <button
                            onClick={handleCheckMobile}
                            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-semibold"
                        >
                            {isLoading ? "Checking..." : "Check Customer"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AepsSettlement3;
